/**
 * `Icon` — set di icone inline (SVG) del design system.
 *
 * Icone leggere, senza dipendenze esterne, disegnate su una griglia 24×24 in
 * stile "stroke" (contorno) coerente. Ereditano il colore dal testo
 * (`stroke="currentColor"`) e la dimensione dalla prop `size`.
 *
 * Sono puramente decorative per default (`aria-hidden`): l'etichetta accessibile
 * va fornita da chi le usa (es. `aria-label` sul pulsante che le contiene).
 */
import type { JSX, SVGProps } from 'react';

/** Nomi delle icone disponibili nel set. */
export type IconName =
  | 'edit'
  | 'trash'
  | 'plus'
  | 'search'
  | 'close'
  | 'check'
  | 'sparkle'
  | 'info'
  | 'settings'
  | 'external'
  | 'user-plus'
  | 'award'
  | 'copy'
  | 'download'
  | 'eye'
  | 'more'
  | 'chevron-up'
  | 'chevrons-up'
  | 'bar-chart';

interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  /** Lato del quadrato in px (default: 16). */
  size?: number;
  /** Etichetta accessibile; se assente l'icona è `aria-hidden`. */
  title?: string;
}

/** Path/elementi SVG per ciascuna icona (griglia 24×24, stroke). */
const PATHS: Record<IconName, JSX.Element> = {
  // Matita (modifica/rinomina).
  edit: (
    <>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </>
  ),
  // Cestino (elimina).
  trash: (
    <>
      <path d="M3 6h18" />
      <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
    </>
  ),
  // Più (crea/nuovo).
  plus: (
    <>
      <path d="M12 5v14M5 12h14" />
    </>
  ),
  // Lente (ricerca).
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </>
  ),
  // X (chiudi). Disegnata centrata nel viewBox 24×24 per un allineamento
  // ottico affidabile (a differenza del glifo unicode "×", che varia col font).
  close: (
    <>
      <path d="M18 6 6 18M6 6l12 12" />
    </>
  ),
  // Spunta (conferma/salva): segno di check centrato nel viewBox 24×24.
  check: (
    <>
      <path d="M20 6 9 17l-5-5" />
    </>
  ),
  // Ingranaggio (impostazioni/configurazione).
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
    </>
  ),
  // Utente con "+" (assegna una persona): busto/testa a sinistra, un piccolo
  // segno più a destra.
  'user-plus': (
    <>
      <path d="M15 20v-1a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v1" />
      <circle cx="8.5" cy="8" r="3.5" />
      <path d="M18 8v6M15 11h6" />
    </>
  ),
  // Coccarda/medaglia (certificazione): cerchio in alto e i due nastri sotto.
  award: (
    <>
      <circle cx="12" cy="8" r="6" />
      <path d="M8.21 13.89 7 22l5-3 5 3-1.21-8.11" />
    </>
  ),
  // Grafico a barre (analisi/insight): asse in basso a sinistra e tre barre
  // verticali di altezza crescente.
  'bar-chart': (
    <>
      <path d="M3 3v18h18" />
      <path d="M8 17v-5M13 17V8M18 17v-9" />
    </>
  ),
  // Copia (due riquadri sovrapposti): copia negli appunti.
  copy: (
    <>
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </>
  ),
  // Link esterno (freccia che esce da un riquadro): apre una risorsa esterna.
  external: (
    <>
      <path d="M15 3h6v6" />
      <path d="M10 14 21 3" />
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    </>
  ),
  // Info (cerchio con "i"): guida/spiegazione contestuale.
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5" />
      <path d="M12 8h.01" />
    </>
  ),
  // Sparkle/AI (valutazione con AIgor). A differenza delle altre icone (stroke),
  // è riempita: fill/stroke sono impostati sui path per renderla nonostante il
  // `fill="none"` del contenitore <svg>.
  sparkle: (
    <>
      <path
        d="M12 3l1.8 4.9L18.9 9.7 14 11.5 12 16.5 10 11.5 5.1 9.7 10.2 7.9 12 3Z"
        fill="currentColor"
        stroke="none"
      />
      <path d="M18.5 14.5l.7 1.9 1.9.7-1.9.7-.7 1.9-.7-1.9-1.9-.7 1.9-.7.7-1.9Z" fill="currentColor" stroke="none" />
    </>
  ),
  // Occhio (rendi visibile/mostra): contorno dell'occhio e pupilla.
  eye: (
    <>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  // Freccia verso il basso su una base (scarica/download).
  download: (
    <>
      <path d="M12 3v12" />
      <path d="M7 10l5 5 5-5" />
      <path d="M5 21h14" />
    </>
  ),
  // Tre puntini verticali (menu azioni "kebab").
  more: (
    <>
      <circle cx="12" cy="5" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="12" cy="19" r="1.6" fill="currentColor" stroke="none" />
    </>
  ),
  // Singolo chevron verso l'alto (segnalazione "Da valutare": interesse medio).
  'chevron-up': (
    <>
      <path d="m6 15 6-6 6 6" />
    </>
  ),
  // Doppio chevron verso l'alto (segnalazione "Promettente": interesse alto).
  'chevrons-up': (
    <>
      <path d="m17 11-5-5-5 5" />
      <path d="m17 18-5-5-5 5" />
    </>
  ),
};

export function Icon({ name, size = 16, title, ...rest }: IconProps) {
  const decorative = title === undefined;
  return (
    <svg
      className="ui-icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={decorative ? undefined : 'img'}
      aria-hidden={decorative ? true : undefined}
      aria-label={title}
      focusable="false"
      {...rest}
    >
      {title !== undefined && <title>{title}</title>}
      {PATHS[name]}
    </svg>
  );
}

export default Icon;
